import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckSquare, Loader2, Clock, AlertTriangle, ChevronRight, Flag } from "lucide-react";
import { myTasksApi } from "@/lib/api";
import useRefreshOnFocus from "@/lib/useRefreshOnFocus";
import { formatTime12 } from "@/lib/time";

// MyPlateWidget — Launchpad teaser for the cross-project My Tasks page.
// Shows up to 5 of the user's most urgent open tasks (overdue + due-soon
// mixed, sorted by due ascending) with a link to the full /my-tasks page.

const PHASE_PILL = {
  pre: "bg-teal-500/15 text-teal-300 border-teal-500/40",
  prod: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  post: "bg-violet-500/15 text-violet-300 border-violet-500/40",
};

function offsetLabel(t) {
  if (t.days_offset == null) return "no due";
  if (t.days_offset < 0) return `${Math.abs(t.days_offset)}d overdue`;
  if (t.days_offset === 0) return "due today";
  if (t.days_offset === 1) return "due tomorrow";
  return `due in ${t.days_offset}d`;
}

export default function MyPlateWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useRefreshOnFocus(() => {
    let cancelled = false;
    myTasksApi
      .list({ includeDone: false })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <article
        data-testid="my-plate-widget-loading"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5 flex items-center gap-2 text-zinc-500 text-sm"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your plate…
      </article>
    );
  }
  if (!data) return null;
  const tasks = (data.tasks || []).slice(0, 5);
  // Total open = every non-done task assigned to me. The backend already
  // filters kanban_status != "done" when includeDone=false, so the raw
  // tasks length is authoritative. Do NOT sum urgency + kanban buckets —
  // as of Feb 2026 those counts are independent (a task can be both LATE
  // and IN PROGRESS), which would cause double-counting here.
  const totalOpen = (data.tasks || []).length;
  const lateCount = data.counts?.late || 0;

  // Empty plate is a great signal — celebrate it but quietly.
  if (totalOpen === 0) {
    return (
      <article
        data-testid="my-plate-widget"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
        style={{ borderLeftColor: "#5eead4", borderLeftWidth: 3 }}
      >
        <div className="text-overline text-teal-400/80 mb-1 flex items-center gap-1.5">
          <CheckSquare className="w-3 h-3" /> My Plate
        </div>
        <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
          Inbox zero. Nice.
        </h2>
        <p className="text-xs text-zinc-500 mt-1">
          Nothing assigned to you is open right now.{" "}
          <button
            type="button"
            onClick={() => navigate("/my-tasks")}
            data-testid="my-plate-widget-open-all"
            className="text-amber-500/90 hover:text-amber-400 transition-colors"
          >
            See all →
          </button>
        </p>
      </article>
    );
  }

  const borderColor = lateCount > 0 ? "#fb7185" : "#fbbf24";

  return (
    <article
      data-testid="my-plate-widget"
      className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
      style={{ borderLeftColor: borderColor, borderLeftWidth: 3 }}
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-overline text-amber-500/80 mb-1 flex items-center gap-1.5">
            <CheckSquare className="w-3 h-3" /> My Plate · {totalOpen} open
          </div>
          <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
            {lateCount > 0 ? (
              <>{lateCount} late, plus what&apos;s coming up</>
            ) : (
              <>Here&apos;s what&apos;s on your plate</>
            )}
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Next few tasks across every project. Click a row to open it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/my-tasks")}
          data-testid="my-plate-widget-open-all"
          className="text-[11px] font-mono uppercase tracking-wide text-zinc-400 hover:text-amber-500 transition-colors"
        >
          See all →
        </button>
      </div>

      <ul className="space-y-1" data-testid="my-plate-widget-list">
        {tasks.map((t) => {
          const isLate = t.status === "late";
          const isMilestone = !!t.is_milestone;
          const phaseCls = PHASE_PILL[t.phase] || "bg-zinc-800 text-zinc-400 border-zinc-700";
          return (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => navigate(`/projects/${t.project_id}/plan?task=${t.id}`)}
                data-testid={`my-plate-row-${t.id}`}
                data-is-milestone={isMilestone ? "true" : undefined}
                className={`group w-full text-left flex items-center gap-3 py-2 px-2 rounded-sm transition-colors ${
                  isMilestone
                    ? "hover:bg-violet-950/30 bg-violet-950/10 border border-violet-500/20"
                    : "hover:bg-zinc-900/60"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    isLate ? "bg-rose-400" : t.status === "due_soon" ? "bg-amber-400" : "bg-sky-400"
                  }`}
                />
                {isMilestone ? (
                  <span
                    data-testid={`my-plate-milestone-badge-${t.id}`}
                    className="px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border shrink-0 bg-violet-500/20 text-violet-200 border-violet-500/50 inline-flex items-center gap-1"
                    title="Milestone"
                  >
                    <Flag className="w-2.5 h-2.5" />
                    Milestone
                  </span>
                ) : (
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border shrink-0 ${phaseCls}`}>
                    {t.phase || "—"}
                  </span>
                )}
                <span className={`flex-1 text-sm truncate min-w-0 ${
                  isMilestone ? "text-violet-100 font-medium" : "text-zinc-200"
                }`}>
                  {t.name}
                </span>
                <span className="hidden sm:inline text-[10px] font-mono text-zinc-500 truncate max-w-[200px]">
                  {t.project_name}
                </span>
                <span
                  className={`text-[10px] font-mono uppercase tracking-wide flex items-center gap-1 shrink-0 ${
                    isLate ? "text-rose-300" : t.status === "due_soon" ? "text-amber-300" : "text-zinc-500"
                  }`}
                >
                  {isLate ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                  {offsetLabel(t)}
                  {t.due_time && (
                    <span className="text-zinc-600">
                      · {formatTime12(t.due_time)}
                    </span>
                  )}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-700 group-hover:text-amber-400 transition-colors shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </article>
  );
}
